import { defineStore } from 'pinia';
import axios from 'axios';
import { useAuthStore } from '@/stores/auth';
import { useMiscStore } from '@/stores/store';
import { subscriptionManager } from '@/core/sockets/subscriptionManager';

// Place telemetry: one event per attempt (HTTP result + exact order-ID side update).
// Missing update within WS_TIMEOUT_MS after a successful place is the drain signal.

const PLACE_TELEMETRY_ROUTE = 'frontend-logging/placeUpdateReceived/';

// Drain = never arrived. Latency percentiles carry "slow"; keep this patient.
const WS_TIMEOUT_MS = 60_000;

// Fraction of matched attempts to emit; misses/failures ALWAYS emit, and sample_rate ships on every event so sampled counts can be normalized.
const SUCCESS_SAMPLE_RATE = (() => {
  const raw = parseFloat(import.meta.env.VITE_PLACE_TELEMETRY_SAMPLE_RATE);
  return Number.isFinite(raw) && raw >= 0 && raw <= 1 ? raw : 1;
})();

function newAttemptId() {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `pa_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function asOrderId(value) {
  if (value == null) return null;
  if (typeof value === 'string' && value.length > 0) return value;
  if (typeof value === 'object' && typeof value.toString === 'function') {
    const s = value.toString();
    return s && s !== '[object Object]' ? s : null;
  }
  return null;
}

// Targets from a place response leg, split by who can produce them.
//
// 'own'   = the session THIS request created (unmatched.orderID). It did not
//           exist until we placed, so only our own update can carry it —
//           confirmation on it is provably ours.
// 'maker' = sessions already on the book that we took from (matched[].orderID).
//           Anyone taking from the same maker order produces an update carrying
//           that id, so confirmation on it is attributable to anyone.
//
// Keeping them apart is what lets the drain rate be read exactly over 'own'
// while take-side numbers are reported as the lower bound they are.
function extractTargetOrderIds(placeSession) {
  const own = [];
  const maker = [];
  if (!placeSession || typeof placeSession !== 'object') return { own, maker };
  const unmatchedId = asOrderId(placeSession.unmatched?.orderID);
  if (unmatchedId) own.push(unmatchedId);
  const matched = Array.isArray(placeSession.matched)
    ? placeSession.matched
    : [];
  for (const m of matched) {
    const id = asOrderId(m?.orderID);
    if (id) maker.push(id);
  }
  return { own, maker };
}

function extractTargetOrderIdsFromSessions(sessions) {
  const legs = Array.isArray(sessions) ? sessions : [sessions];
  const own = new Set();
  const maker = new Set();
  for (const session of legs) {
    const t = extractTargetOrderIds(session);
    for (const id of t.own) own.add(id);
    for (const id of t.maker) maker.add(id);
  }
  // An id that is both (a leg posted it, another leg took it) counts as 'own':
  // we know we caused that one.
  for (const id of own) maker.delete(id);
  return { own, maker };
}

function orderIdsFromUpdate(payload) {
  const ids = [];
  if (!payload) return ids;
  const triggerId = asOrderId(payload.triggerOrderId ?? payload.orderID);
  if (triggerId) ids.push(triggerId);
  if (Array.isArray(payload.sideOrders)) {
    for (const o of payload.sideOrders) {
      const id = asOrderId(o?.id ?? o?._id);
      if (id) ids.push(id);
    }
  }
  return ids;
}

// Socket state at emit time — on a timeout, "was the price socket even connected?" is the first diagnostic question.
function socketStatusSnapshot() {
  const miscStore = useMiscStore();
  return {
    user_socket_status: miscStore.socketStatus.userFeedSocket || null,
    price_socket_status: miscStore.socketStatus.priceUpdateSocket || null,
  };
}

// keepalive routes through fetch() so the request survives page unload (sendBeacon semantics, but with the auth header intact).
function reportPlaceUpdateReceived(payload, { keepalive = false } = {}) {
  const authStore = useAuthStore();
  if (!authStore.token) return;

  const url = `${import.meta.env.VITE_APP_API_ENDPOINT}/${PLACE_TELEMETRY_ROUTE}`;
  const body = { ...payload, ...socketStatusSnapshot() };
  const headers = {
    'Content-Type': 'application/json',
    authorization: authStore.token,
  };

  if (keepalive && typeof fetch === 'function') {
    try {
      fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Telemetry must never affect placement UX.
    }
    return;
  }

  axios.post(url, body, { headers, timeout: 5000 }).catch(() => {
    // Telemetry must never affect placement UX.
  });
}

export {
  WS_TIMEOUT_MS,
  extractTargetOrderIds,
  extractTargetOrderIdsFromSessions,
  orderIdsFromUpdate,
  asOrderId,
};

export const usePlaceTrackerStore = defineStore('placeTracker', () => {
  // id -> attempt state. Plain Map — nothing renders from it.
  const attempts = new Map();

  // Open an attempt right before the place POST.
  // meta: reporting dims (orderType/gameId/marketType/isLive) + subscription context (leagueId/sportId).
  function register(meta) {
    const id = newAttemptId();
    const safeMeta = meta || {};
    const attempt = {
      // waiting_for_http | waiting_for_ws | done
      kind: 'waiting_for_http',
      tRequestSent: performance.now(),
      wasHidden:
        typeof document !== 'undefined' ? document.hidden === true : false,
      meta: safeMeta,
      // orderId -> ms since tRequestSent (WS can beat HTTP)
      firstSeenTimestamps: new Map(),
      // Ids first seen while still waiting_for_http — recorded before we knew
      // what to look for, so they are the least attributable confirmations.
      bufferedIds: new Set(),
      targetOrderIds: null,
      ownTargetIds: null,
      makerTargetIds: null,
      confirmSource: null,
      confirmTarget: null,
      httpStatus: null,
      httpMs: null,
      wsMs: null,
      done: false,
      timer: null,
    };
    attempts.set(id, attempt);
    attempt.timer = setTimeout(() => onTimeout(id), WS_TIMEOUT_MS);
    return id;
  }

  // Returns { orderId, ms } for the earliest seen target, or null.
  function earliestFirstSeen(a, targetIds) {
    let best = null;
    for (const orderId of targetIds || []) {
      const seen = a.firstSeenTimestamps.get(orderId);
      if (seen == null) continue;
      if (best == null || seen < best.ms) best = { orderId, ms: seen };
    }
    return best;
  }

  function confirmIfTargetsSeen(id, a) {
    if (!a.targetOrderIds || a.targetOrderIds.size === 0) return false;

    // Prefer an 'own' id even when a 'maker' id was seen marginally earlier:
    // exactness is worth more than a few ms of ws_ms precision, because only
    // 'own' proves the update was caused by this request.
    const ownHit = earliestFirstSeen(a, a.ownTargetIds);
    const hit = ownHit || earliestFirstSeen(a, a.makerTargetIds);
    if (hit == null) return false;

    a.wsMs = Math.round(hit.ms);
    a.confirmTarget = ownHit ? 'own' : 'maker';
    a.confirmSource = a.bufferedIds.has(hit.orderId) ? 'buffered' : 'live';
    finalize(id, 'matched');
    return true;
  }

  // Non-ok HTTP finalizes now (no side update expected). Ok extracts target order IDs and waits for an exact match.
  function recordHttpResponse(id, httpStatus, placeSessions = null) {
    const a = attempts.get(id);
    if (!a || a.done || a.kind !== 'waiting_for_http') return;

    a.httpStatus = httpStatus;
    a.httpMs = Math.round(performance.now() - a.tRequestSent);

    if (httpStatus !== 'ok') {
      finalize(id, httpStatus);
      return;
    }

    const targets = extractTargetOrderIdsFromSessions(placeSessions);
    const targetIds = [...targets.own, ...targets.maker];
    if (targetIds.length === 0) {
      // Server said ok but gave us nothing to watch — treat as rejected for telemetry.
      a.httpStatus = 'rejected';
      finalize(id, 'rejected');
      return;
    }

    a.targetOrderIds = new Set(targetIds);
    a.ownTargetIds = targets.own;
    a.makerTargetIds = targets.maker;
    a.kind = 'waiting_for_ws';
    if (confirmIfTargetsSeen(id, a)) return;
  }

  // Fed every orderUpdate before view-based filtering. Exact order-ID match only.
  function observeOrderUpdate(payload) {
    if (!payload || attempts.size === 0) return;
    const seenIds = orderIdsFromUpdate(payload);
    if (seenIds.length === 0) return;

    const at = performance.now();
    for (const [id, a] of attempts) {
      if (a.done) continue;

      if (a.kind === 'waiting_for_http') {
        // ID unknown yet — record first-seen for every order id on the update
        // (posts: our session; takes: maker session still on the book).
        //
        // KNOWN BOUND (takes only): a post's target session does not exist
        // until our own place creates it, so only our own update can land
        // here — posts are exact. A take's targets are MAKER sessions that
        // were already on the book, so a third party taking from the same
        // maker order inside our HTTP window records a first-seen here and
        // resolves us as 'matched' off their event, with a ws_ms that
        // measures their update rather than ours.
        //
        // Window is the HTTP duration only, but it biases the take drain
        // rate DOWN — takes look healthier than they are. triggerOrderId
        // does not close this: a foreign take on the same maker order
        // carries the same trigger id. A real fix needs taker identity or a
        // request correlation id on the update. Treat take-side drain
        // numbers as a lower bound; post-side numbers are exact.
        for (const orderId of seenIds) {
          if (!a.firstSeenTimestamps.has(orderId)) {
            a.firstSeenTimestamps.set(orderId, at - a.tRequestSent);
            // Recorded before the response told us what to look for.
            a.bufferedIds.add(orderId);
          }
        }
        continue;
      }

      if (a.kind === 'waiting_for_ws') {
        let hit = false;
        for (const orderId of seenIds) {
          if (!a.targetOrderIds.has(orderId)) continue;
          if (!a.firstSeenTimestamps.has(orderId)) {
            a.firstSeenTimestamps.set(orderId, at - a.tRequestSent);
          }
          hit = true;
        }
        if (hit) confirmIfTargetsSeen(id, a);
      }
    }
  }

  function onTimeout(id) {
    const a = attempts.get(id);
    if (!a || a.done) return;

    // Last chance: targets may already be in firstSeen from a WS-before-HTTP race
    // where HTTP arrived in the same tick as the timer (unlikely) — confirm if possible.
    if (a.kind === 'waiting_for_ws' && confirmIfTargetsSeen(id, a)) return;

    if (a.kind === 'waiting_for_http') {
      // No HTTP response at all — we can't even say the bet was placed, so this
      // is NOT a delivery drain. Kept distinct from 'timeout' (per Ben's state
      // machine sketch, which separates no_http_response from no_ws_update)
      // because the two have different causes and different owners: this one
      // means the place request itself never came back.
      //
      // Collapsing them would put API/network failures in the drain-rate
      // numerator, and they correlate — whatever stalls HTTP also stalls the
      // websocket — so the drain rate would spike hardest exactly during an
      // incident that has nothing to do with price-stream delivery.
      finalize(id, 'no_http_response');
      return;
    }

    // HTTP ok but the target order never appeared on the stream — the drain.
    finalize(id, 'timeout');
  }

  function finalize(id, outcome, { keepalive = false } = {}) {
    const a = attempts.get(id);
    if (!a || a.done) return;
    a.done = true;
    a.kind = 'done';
    if (a.timer) clearTimeout(a.timer);
    attempts.delete(id);

    // Emit policy: always emit misses/failures; sample successes.
    const isSuccess = outcome === 'matched';
    if (
      isSuccess &&
      SUCCESS_SAMPLE_RATE < 1 &&
      Math.random() > SUCCESS_SAMPLE_RATE
    ) {
      return;
    }

    // A timeout after navigating away is expected, not a drain; covers() is always true when subscriptions are disabled.
    const stillSubscribed = subscriptionManager.covers(
      a.meta.gameId,
      a.meta.leagueId,
      a.meta.sportId,
    );

    reportPlaceUpdateReceived(
      {
        place_attempt_id: id,
        // 'place' (take/post) vs 'edit' (cancel + repost). Split out because an
        // edit's target is the NEW session id, which makes it exact like a post
        // — unlike a take, whose drain number is only a lower bound.
        operation: a.meta.operation ?? 'place',
        order_type: a.meta.orderType ?? null,
        game_id: a.meta.gameId ?? null,
        market_type: a.meta.marketType ?? null,
        is_live: a.meta.isLive ?? null,
        http_status: a.httpStatus,
        outcome,
        // Exact order-ID match only; null when no update observed.
        match_confidence: outcome === 'matched' ? 'self' : null,
        // How the confirmation was reached. 'own' is provably ours; 'maker' is
        // attributable to any taker of the same order, so the exact drain rate
        // is the one filtered to confirm_target=own.
        confirm_target: outcome === 'matched' ? a.confirmTarget : null,
        // 'buffered' = the timestamp predates knowing the targets (the WS beat
        // the HTTP response); 'live' = it arrived after. buffered+maker is the
        // least attributable combination.
        confirm_source: outcome === 'matched' ? a.confirmSource : null,
        http_ms: a.httpMs,
        ws_ms: a.wsMs,
        total_ms: Math.round(performance.now() - a.tRequestSent),
        was_hidden: a.wasHidden,
        still_subscribed: stillSubscribed,
        sample_rate: isSuccess ? SUCCESS_SAMPLE_RATE : 1,
      },
      { keepalive },
    );
  }

  // Flush open attempts as 'abandoned' on pagehide (not visibilitychange, so tab switches don't flush); bfcache restores just no-op their dead timers.
  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', () => {
      for (const id of Array.from(attempts.keys())) {
        finalize(id, 'abandoned', { keepalive: true });
      }
    });
  }

  return { register, recordHttpResponse, observeOrderUpdate };
});

export default usePlaceTrackerStore;
